import { IEmployeeErrorData, IJob, ILabEmployee, IUpdateLabEmployeeResult } from 'typings/employeeErrorData';
import { dataManagerFactory, configurationsStore } from 'utils/MobxDataManager/singletons';
import {
    IEmployeeDataManager,
    IDiagnosticsManager,
    IAccountDataManager,
    IEventDataManager
} from 'typings/dataManagers';
import { EmployeeErrorData } from './employeeErrorData';
import { FieldDisplayName, FieldName, Roles, EventTypes, EntityTypes } from 'typings/enums';
import { IMissingUser, ICurrentEmails } from 'typings/diagnostics';
import { observable, computed, action, makeObservable } from 'mobx';
import { IEmployeeIdMap, IEmployee } from 'typings/employee';

const fieldNameConversion:{ [id: string]: FieldDisplayName } = {
    firstName: FieldDisplayName.FirstName,
    lastName: FieldDisplayName.LastName,
    orgJobTitle: FieldDisplayName.JobTitleInternal,
    employeeNumber: FieldDisplayName.EmployeeSourceId,
    dateOfHire: FieldDisplayName.HireDate,
    managerNumber: FieldDisplayName.ManagerSourceId,
    workEmail: FieldDisplayName.WorkEmail,
    annualOrHourly: FieldDisplayName.AnnualOrHourly,
    currency: FieldDisplayName.IsoCurrencyCode,
    countryCode: FieldDisplayName.CountryCode,
    standardWorkHours: FieldDisplayName.StandardWorkHours,
    missingUser: FieldDisplayName.MissingUser,
    duplicateEmail: FieldDisplayName.DuplicateEmail,
    all: FieldDisplayName.All
};

const dupeEmailText = 'Multiple employees with same';

export class TeamErrorData {

    _masterAccountId: string;
    _userId: string;
    _allEmails: ICurrentEmails;
    @observable private _teamErrors: IEmployeeErrorData[];
    @observable private _deletedEmployees: IEmployee[];
    private _employeeDataManager: IEmployeeDataManager;
    private _diagnosticsManager: IDiagnosticsManager;
    private _accountDataManager: IAccountDataManager;
    private _eventDataManager: IEventDataManager;
    @observable private _errorCounts: { [id: string]: number };
    private _employeeIds: IEmployeeIdMap;
    private _jobs: IJob[];

    constructor(masterAccountId: string, userId: string) {
        makeObservable(this);
        this._teamErrors = [];
        this._deletedEmployees = [];
        this._errorCounts = {};
        this._employeeDataManager = dataManagerFactory.getEmployeeDataManager();
        this._diagnosticsManager = dataManagerFactory.getDiagnosticsManager();
        this._accountDataManager = dataManagerFactory.getAccountDataMaanger();
        this._eventDataManager = dataManagerFactory.getEventDataManager();
        this._masterAccountId = masterAccountId;
        this._userId = userId;
        this._allEmails = {};

        this.deleteEmployees = this.deleteEmployees.bind(this);
    }

    async requestEmployeeErrorData(blockUi: boolean = false): Promise<IEmployeeErrorData[]> {
        if (this._teamErrors?.length) {
            return this._teamErrors;
        }
        const deletedEmployeesCall = this._employeeDataManager.getDeletedEmployees(this._masterAccountId);
        const errorData = (await this._employeeDataManager.getEmployeeErrorData(this._masterAccountId, blockUi)) ?? [];

        errorData.map(employeeErrors => {
            this._teamErrors.push(new EmployeeErrorData(employeeErrors));
        });
        const emailErrors = await this._diagnosticsManager.getEmailIssues(this._masterAccountId);
        this._allEmails = emailErrors?.allEmails ?? {};
        this._deletedEmployees = (await deletedEmployeesCall) ?? [];

        this.addDuplicateEmailsToErrorDataArray(emailErrors?.dupeEmails);
        return this.addMissingUsersToErrorDataArray(emailErrors?.missing);
    }

    async getEmployeeIds(): Promise<IEmployeeIdMap> {
        this._employeeIds = (await this._employeeDataManager.getEmployeeIds(this._masterAccountId)) ?? {};
        return this._employeeIds;
    }

    async getJobs(): Promise<IJob[]> {
        this._jobs = (await this._employeeDataManager.getJobData(this._masterAccountId)) ?? [];
        return this._jobs;
    }

    async updateLabEmployee(labEmployee: Nullable<ILabEmployee>): Promise<Nullable<IUpdateLabEmployeeResult>> {
        return await this._employeeDataManager.updateLabEmployee(this._masterAccountId, labEmployee);
    }

    addDuplicateEmailsToErrorDataArray(
        duplicateEmails?: {[index: string]: IMissingUser[]}): IEmployeeErrorData[] {
        if (!duplicateEmails || !Object.keys(duplicateEmails).length) {
            return this._teamErrors;
        }
        this._teamErrors.forEach(employee => {
            const email = employee.teamEmployee?.workEmail ?? '';
            const employeeId = employee.labEmployee?.employeeNumber ?? '';
            const dupeEmailArray = duplicateEmails[email];
            if (dupeEmailArray) {
                employee.errorDetails[FieldName.DuplicateEmail] = dupeEmailText;
                const newArray = dupeEmailArray.filter(employee => employee.employeeSourceId !== employeeId);
                duplicateEmails[email] = newArray;
            }
        });

        Object.values(duplicateEmails).forEach(dupeEmployeeArray => {
            dupeEmployeeArray.forEach(employee => {
                this._teamErrors.push(this.createNewErrorDataEntry(
                    employee, dupeEmailText, FieldName.DuplicateEmail, FieldDisplayName.DuplicateEmail));
            });
        });
        return this._teamErrors;
    }

    addMissingUsersToErrorDataArray(
        missingUsers?: IMissingUser[]): IEmployeeErrorData[] {
        if (!missingUsers || !missingUsers?.length) {
            return this._teamErrors;
        }
        const missingUserObject: {[id: string]: IMissingUser} = {};
        // assign missing users to an object then if they exist in the error data already remove them
        // from the object and assign missing user to the employee error details
        missingUsers.forEach(user => {
            missingUserObject[user.employeeSourceId] = user;
        });
        this._teamErrors.forEach(employee => {
            const key = employee.labEmployee?.employeeNumber ?? '';
            if (missingUserObject[key]) {
                employee.errorDetails[FieldName.MissingUser] = 'missing';
                delete missingUserObject[key];
            }
        });
        // for all users who are still missing and not in the error data, create an entry and add them
        // into the list
        Object.values(missingUserObject).forEach(user => {
            this._teamErrors.push(this.createNewErrorDataEntry(
                user, 'missing', FieldName.MissingUser, FieldDisplayName.MissingUser));
        });
        return this._teamErrors;
    }

    countErrorData(): { [id: string]: number } {
        if (!this._teamErrors.length) {
            return {};
        }
        let totalCount: number = 0;
        this._errorCounts = {};
        this._teamErrors.map(employee => {
            Object.keys(employee.errorDetails).map(errors => {
                if (this._errorCounts[errors]) {
                    this._errorCounts[errors] += 1;
                    totalCount += 1;
                } else {
                    this._errorCounts[errors] = 1;
                    totalCount += 1;
                }
            });
        });
        this._errorCounts[FieldDisplayName.All] = totalCount;
        return this._errorCounts;
    }

    createOptions(): [{ [id: string]: string }] | [] {
        if (!Object.keys(this._errorCounts).length) {
            return [];
        }
        const selectOptions: [{ [id: string]: string }] | [] =
            [{'text': `${FieldDisplayName.All} (${this._errorCounts[FieldDisplayName.All]})`,
                'value': FieldDisplayName.All}];
        Object.keys(this._errorCounts).map(error => {
            if (error === FieldDisplayName.All) {
                return;
            }
            selectOptions.push({'text': `${fieldNameConversion[error]} (${this._errorCounts[error]})`, 'value': error});
        });
        return selectOptions;
    }

    createMPayTypeOptions(): [{ text: string, value: Nullable<string> }] | []  {
        const payTypeOptions: [{ text: string, value: Nullable<string> }] = [{ text: 'Select option', value: '--' }];

        payTypeOptions.push(
            { text: 'Annual', value: 'Annual' },
            { text: 'Hourly', value: 'Hourly' }
        );

        return payTypeOptions;
    }


    createJobsOptions(): [{ text: string, value: Nullable<string> }] | []  {
        if (!this._jobs) {
            return [];
        }

        const jobOptions: [{ text: string, value: Nullable<string> }] = [{ text: 'Select Job Title', value: '--' }];

        jobOptions.push(...this._jobs.map((job) => {
            return {
                text: `${job.code} - ${job.title} - ${job.jobCodeKey}`,
                value: `${job.title}-${job.orgJobId}-${job.currency}`
            };
        }));

        return jobOptions;
    }

    @action
    setNewErrorData(errorData: IEmployeeErrorData[]) {
        this._teamErrors = errorData;
    }


    private createNewErrorDataEntry(
        user: IMissingUser,
        errorDetail: string,
        fieldName: string,
        FieldDisplayName: string): IEmployeeErrorData {
        return {
            teamEmployee: {
                countryCode: user.countryCode,
                workEmail: user.workEmail
            },
            labEmployee: {
                annualOrHourly: null,
                employeeNumber: user.employeeSourceId,
                firstName: user.firstName,
                lastName: user.lastName,
                currency: user.isoCurrencyCode,
                standardWorkHours: user.annualStandardWorkHours,
                managerNumber: user.managerSourceId,
                orgJobTitle: FieldDisplayName,
                dateOfHire: user.hireDate ? new Date(user.hireDate) : null
            },
            errorDetails: {
                [fieldName]: errorDetail
            }
        };
    }

    @computed
    get showInvalidEmployeeAlert(): boolean {
        const fieldsToTriggerBanner = new Set(['annualOrHourly', 'employeeNumber', 'currency','standardWorkHours','managerNumber','dateOfHire', 'duplicateEmail', 'workEmail', 'countryCode']);
        if(!this._teamErrors?.length) {
            return false;
        }

        for (let i = 0; i < this._teamErrors.length; i++) {
            const errors = Object.keys(this._teamErrors[i].errorDetails);
            for (let j = 0; j < errors.length; j++) {
                if(fieldsToTriggerBanner.has(errors[j]))
                {
                    return true;
                }
            }
        }

        return false;
    }

    get TeamErrorData(): IEmployeeErrorData[] {
        return this._teamErrors;
    }

    get deletedEmployees(): IEmployee[] {
        return this._deletedEmployees;
    }

    @action
    clearTeamDataErrors(): void {
        this._teamErrors = [];
    }

    updateErrorCountOnSubmitSuccess(index: number): IEmployeeErrorData[] {
        const currentErrors = this._teamErrors.slice(0);
        const employeeErrorData = currentErrors[index];
        if (!employeeErrorData.errorDetails.missingUser) {
            currentErrors.splice(index, 1);
        } else {
            const newEmployeeErrorData: IEmployeeErrorData = {...employeeErrorData};
            newEmployeeErrorData.errorDetails = { 'missingUser': 'missing' };
            currentErrors[index]  = newEmployeeErrorData;
        }

        this._teamErrors = currentErrors;
        return this._teamErrors;
    }

    async inviteUser(user: IUserInvite): Promise<void|null> {
        const userInviteRequest = {
            userEmail: user.userEmail,
            roleIds: [Roles.Manager],
            firstName: user.firstName,
            lastName: user.lastName,
            product: 'teampay',
            sendEmail: true

        } as IUserInviteRequest;
        return await this._accountDataManager.sendUserInvite(
            userInviteRequest, this._masterAccountId);
    }

    async ignoreUser(user: Nullable<IUserInvite>): Promise<void|null> {
        if (!user) {
            return;
        }
        return await this._employeeDataManager.ignoreUserError(this._masterAccountId, user.employeeId);
    }

    @action
    async deleteEmployees(employees: IEmployee[], removeUsers: boolean): Promise<void> {
        const events = employees
            .map(ee => ({
                employeeSourceId: ee.employeeSourceId,
                useEmployeeTopic: true,
                eventType: EventTypes.Terminate,
                entityType: EntityTypes.Employee,
                userId: this._userId,
                data: {
                    terminationDate: new Date(Date.now()).toUTCString(),
                    reason: 'Marked as terminated by HR Admin'
                },
                metadata: {
                    affectWorkflow: true
                }
            } as IEmployeeEvent));

        await this._eventDataManager.sendEvents(this._masterAccountId, null, events);

        if (removeUsers) {
            const cycleId = configurationsStore.currentCycleId;

            const removeUserEvents = employees
                .filter(ee => ee.isUser)
                .map(ee => ({
                    employeeSourceId: ee.employeeSourceId,
                    useEmployeeTopic: false,
                    eventType: EventTypes.RemoveUserEvent,
                    entityType: EntityTypes.EmployeeCycle,
                    userId: this._userId,
                    data: {
                        updatedByUserId: this._userId
                    },
                    metadata: undefined
                } as IEmployeeEvent));

            await this._eventDataManager.sendEvents(this._masterAccountId, cycleId, removeUserEvents);
        }

        this._deletedEmployees = [];
    }
}